import { useCallback } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { listEmailThreadsV2 } from 'services/mailbox/mailboxServiceV2';
import { mailboxV3Keys } from './mailboxV3QueryKeys';

const DEFAULT_LIMIT = 20;

/**
 * Hook for fetching paginated email threads using React Query's useInfiniteQuery.
 * Replaces useEmailThreadsV2 for V3 — eliminates manual offset state, append useEffect,
 * and JSON.stringify dependency hacks.
 *
 * Filter changes automatically reset the list because the query key changes.
 * Pages are flattened via the `select` option — no local threads state needed.
 *
 * @param {object} params
 * @param {string}   [params.searchKeyword]
 * @param {boolean}  [params.isSent=false]
 * @param {boolean}  [params.isUnread=false]
 * @param {object}   [params.filters]
 * @param {number}   [params.limit=20]
 * @param {boolean}  [params.isEmailLog=false]
 * @param {boolean}  [params.enabled=true] - When false, the list query is disabled (e.g. until a case id exists).
 * @param {'my_email'|'entire_firm'} [params.viewType] - API view_type (my vs firm scope).
 */
const useEmailThreadsV3 = ({
  searchKeyword = '',
  isSent = false,
  isUnread = false,
  filters = {},
  limit = DEFAULT_LIMIT,
  isEmailLog = false,
  enabled = true,
  viewType,
} = {}) => {
  const queryClient = useQueryClient();

  // Stable filter key object — React Query serialises this as the cache key.
  // Changing any field automatically invalidates and resets the infinite list.
  const filterKey = {
    searchKeyword,
    isSent,
    isUnread: isUnread || undefined,
    isEmailLog,
    caseId: filters.caseId,
    mailDateLower: filters.mailDateLower || filters.mail_date_lower,
    mailDateUpper: filters.mailDateUpper || filters.mail_date_upper,
    mappingStatus: filters.mappingStatuses?.join(','),
    filterByUser: filters.userFilter?.join(','),
    limit,
    ...(viewType ? { viewType } : {}),
  };

  const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery(
    mailboxV3Keys.threads(filterKey),
    ({ pageParam = 0 }) => listEmailThreadsV2({ ...filterKey, offset: pageParam }),
    {
      enabled,
      /**
       * Returns the offset for the next page, or undefined when exhausted.
       * `pagination.offset` is the offset that was used for this page, so
       * next offset = current offset + page size.
       */
      getNextPageParam: (lastPage) => {
        const { has_more, offset } = lastPage?.data?.pagination ?? {};
        return has_more ? (offset ?? 0) + limit : undefined;
      },

      /**
       * `select` transforms / flattens the infinite pages into the shape
       * consumers expect — runs synchronously inside React Query so no
       * extra useEffect / useState is needed.
       */
      select: (infiniteData) => ({
        threads: infiniteData.pages.flatMap((page, pageIndex) =>
          (page?.data?.email_threads ?? []).map((thread, i) => ({
            ...thread,
            index: pageIndex * limit + i,
          }))
        ),
        totalCount: infiniteData.pages[0]?.data?.pagination?.total_count ?? 0,
      }),

      // Must stay fresh when switching Inbox/Sent — same tab uses different
      // query keys; a long staleTime would serve cached inbox with no refetch.
      staleTime: 0,
    }
  );

  const threads = data?.threads ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const refresh = useCallback(
    () => {
      queryClient.invalidateQueries({ queryKey: mailboxV3Keys.threads(filterKey) });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, JSON.stringify(filterKey)]
  );

  return {
    threads,
    totalCount,
    loading: isLoading || (isFetching && !isFetchingNextPage),
    paginationLoading: isFetchingNextPage,
    hasMore: hasNextPage ?? false,
    handleLoadMore,
    refresh,
    refetch,
  };
};

export default useEmailThreadsV3;
